import {
  FormField,
  Select,
} from '@cloudscape-design/components';
import { PartTypes, PPartRetiredStatuses } from '@parallel-fluidics/shared/constants';
import { getPPartId, pPartFromFirestore } from '@parallel-fluidics/shared/models/projects/parts';
import {
  collection,
  getFirestore,
  query,
  where,
} from 'firebase/firestore';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo } from 'react';

import useRealtimeDocs from '../../../hooks/use-realtimeDocs';

export default function ToolSelector({
  onChange,
  linkedParts = [],
  projectDocId = '',
  selected = null,
  error = null,
}) {
  const toolsQuery = useMemo(() => {
    if (!projectDocId) return null;
    return query(
      collection(getFirestore(), 'projects', projectDocId, 'parts'),
      where('type', '==', PartTypes.TOOL),
    );
  }, [projectDocId]);

  const { data: tools, status: toolsStatus } = useRealtimeDocs({
    query: toolsQuery,
    transformData: pPartFromFirestore,
  });

  const toolOptions = useMemo(() => {
    if (!tools) return [];

    return tools.reduce((acc, tool) => {
      if (!PPartRetiredStatuses.includes(tool.status)) {
        const pPartId = getPPartId(tool);
        acc.push({
          id: tool.id,
          value: pPartId,
          label: `${pPartId} (${tool.cavities.map((cavity) => cavity.name).join(', ')})`,
          labelTag: tool.status,
          cavities: tool.cavities,
        });
      }
      return acc;
    }, []);
  }, [tools]);

  const isLinked = useCallback((tool) => linkedParts.some(
    (row) => row.projectDocId === projectDocId && row.pPartDocId === tool.id,
  ), [linkedParts, projectDocId]);

  // The form keeps the selection across modal closes. Once the tool list
  // has loaded, clear a selection that's no longer offered (retired or
  // deleted since) so a run can't record it — mirrors PistonSelector.
  // Declared before the auto-select effect: effects run in order, so when
  // the selected tool retires while a linked replacement exists, the clear
  // happens first and the auto-selected replacement wins.
  useEffect(() => {
    if (toolsStatus !== 'success' || !selected?.value) return;
    if (!toolOptions.some((option) => option.value === selected.value)) onChange({});
  }, [toolsStatus, toolOptions, selected, onChange]);

  // auto select the tool if selected step linked with a tool
  useEffect(() => {
    if (!linkedParts?.length || !toolOptions?.length) return;
    // auto-select if exactly one linked tool found
    const findTools = toolOptions.filter(isLinked);
    if (findTools.length !== 1) return;
    const [findTool] = findTools;
    if (findTool) onChange(findTool);
  }, [linkedParts, toolOptions, onChange, isLinked]);

  // A linked tool that's hidden (Obsolete/Broken) can't be auto-selected —
  // say so instead of leaving the field silently empty.
  const warningText = useMemo(() => {
    const hiddenLinked = (tools || [])
      .filter((tool) => PPartRetiredStatuses.includes(tool.status) && isLinked(tool));
    if (!hiddenLinked.length) return undefined;
    return hiddenLinked
      .map((tool) => `Linked tool ${getPPartId(tool)} is ${tool.status}`)
      .join('. ');
  }, [tools, isLinked]);

  return (
    <FormField
      label="Tool name"
      stretch
      errorText={error}
      warningText={warningText}
      constraintText={(
        <span>
          {'Don\'t see the tool you\'re looking for? Add a new tool '}
          <a target="_blank" href={`/projects/${projectDocId}`} rel="noreferrer">here</a>
          .
        </span>
      )}
    >
      <Select
        selectedOption={selected}
        onChange={({ detail }) => onChange(detail.selectedOption)}
        options={toolOptions}
        filteringType="auto"
        placeholder="Choose a tool"
        empty="No tools found for this project"
      />
    </FormField>
  );
}

ToolSelector.propTypes = {
  onChange: PropTypes.func.isRequired,
  projectDocId: PropTypes.string,
  selected: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
  error: PropTypes.string,
  linkedParts: PropTypes.arrayOf(PropTypes.shape({
    projectDocId: PropTypes.string,
    pPartDocId: PropTypes.string,
  })),
};
